import { type MouseEvent, useCallback, useRef } from 'react';

import { useCloneCanvasNodes } from '../../useCloneCanvasNodes';
import { commitDuplicateByDrag, type DuplicateByDragSession, startDuplicateByDrag } from '../duplicateByDrag';
import { useCommandHistory } from './useCommandHistory';

/**
 * Handles alt+drag to duplicate nodes on the canvas.
 *
 * @returns Object with drag handlers
 * - onDragStart: If alt is held, clones the dragged nodes and adds them to the
 *   canvas at the original positions for immediate visual feedback.
 * - onDragStop: Creates an undo/redo command. Execute moves originals to their
 *   new positions and re-adds clones on redo. Undo removes clones and restores
 *   original positions. Returns true if alt+drag was active so the caller can
 *   skip the normal move command.
 */
export const useDuplicateByDragCommand = () => {
  const execute = useCommandHistory((state) => state.execute);
  const cloneObjects = useCloneCanvasNodes();
  const sessionRef = useRef<DuplicateByDragSession | null>(null);

  const onDragStart = useCallback(
    (event: MouseEvent, nodes: { id: string }[]) => {
      if (!event.altKey) {
        sessionRef.current = null;
        return;
      }

      const session = cloneObjects(nodes);
      sessionRef.current = session;
      startDuplicateByDrag(session);
    },
    [cloneObjects],
  );

  const onDragStop = useCallback(
    (
      nodes: { id: string; position: { x: number; y: number } }[],
      previousPositions: { id: string; position: { x: number; y: number } }[],
    ) => {
      const session = sessionRef.current;

      if (!session) {
        return false;
      }

      sessionRef.current = null;
      commitDuplicateByDrag(execute, session, nodes, previousPositions);

      return true;
    },
    [execute],
  );

  return { onDragStart, onDragStop };
};
